import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, DashboardLayout, Button, ProjectWorkflowStatusBadge } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { ProjectsListTable, type ProjectsListColumn } from '../components/ProjectsListTable';
import { useColumnOrder } from '../hooks/useColumnOrder';
import { ProjectCompanyName } from '../components/ProjectCompanyName';
import { personApi } from '../services/api';
import { loadGlobalTemplate, projectDisplayStatusOptions, getStatusLabel as getStatusLabelUtil, type StatusStep } from '../services/projectStatusUtils';
import { projectDisplayStatus } from '../utils/milestoneWorkflowStatus';
import { useListSort } from '../hooks/useListSort';
import { DEFAULT_PROJECT_LIST_SORT, isCompletedProject, lastActivityColumn, projectListMobileSort } from '../utils/projectListSort';
import { formatLastActivity } from '../utils/lastActivity';

const COLUMN_ORDER: string[] = ['projectNumber', 'description', 'companyName', 'totalContractAmount', 'status', 'startDate', 'endDate', 'lastActivityAt'];
const PREFERENCE_KEY = 'personProjectListColumnOrder';

/** The list, or with `completed` the list under "Avslutade Projekt": the same
 *  table with only the completed projects (ClickUp 869fc31mm). */
export function PersonProjectList({ completed = false }: { completed?: boolean } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [projects, setProjects] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [templateSteps, setTemplateSteps] = useState<StatusStep[]>([]);
  const [visibleRows, setVisibleRows] = useState<any[]>([]);
  const [columnOrder, handleReorder] = useColumnOrder(PREFERENCE_KEY, COLUMN_ORDER);
  const [listSort, handleListSort] = useListSort('personProjectListSort', DEFAULT_PROJECT_LIST_SORT, COLUMN_ORDER);
  // No "Avslutat" option: completed projects are listed under "Avslutade Projekt".
  const statusOptions = useMemo(
    () => projectDisplayStatusOptions(templateSteps).filter(option => option.value !== 'completed'),
    [templateSteps]
  );
  const listedProjects = useMemo(
    () => projects.filter(project => isCompletedProject(project) === completed),
    [projects, completed]
  );
  const getStatusLabel = (status: string) => getStatusLabelUtil(status, templateSteps) || status;

  useEffect(() => {
    loadProjects();
    loadGlobalTemplate().then(setTemplateSteps).catch(() => {});
  }, []);

  const loadProjects = async () => {
    try {
      const data = await personApi.getProjects();
      setProjects(data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatAmount = (amount: number) => new Intl.NumberFormat('sv-SE').format(amount || 0);

  const openProject = (project: any) => navigate(`/person/projects/${project.id}`);

  // Memoized: ProjectsListTable's onVisibleRowsChange effect fires whenever this
  // array's reference changes, so a fresh array every render would re-trigger
  // setVisibleRows in an infinite loop.
  const columns: ProjectsListColumn<any>[] = useMemo(() => [
    { field: 'projectNumber', label: t('projects.columns.projectNumber'), render: p => `#${p.projectNumber}` },
    { field: 'description', label: t('projects.columns.description'), render: p => p.description },
    { field: 'companyName', label: t('projects.columnCompanyName', 'Hantverkare'), render: p => <ProjectCompanyName companyName={p.companyName} /> },
    { field: 'totalContractAmount', label: t('projects.columns.totalContractAmount'), filter: 'none', sortValue: p => p.totalContractAmount || 0, render: p => `${formatAmount(p.totalContractAmount)} kr` },
    {
      field: 'status',
      label: t('projects.columns.status'),
      filter: completed ? 'none' : 'select',
      selectAllLabel: t('projects.allStatuses'),
      selectOptions: statusOptions,
      filterValue: p => projectDisplayStatus(p),
      sortValue: p => projectDisplayStatus(p),
      render: p => <ProjectWorkflowStatusBadge project={p} steps={templateSteps} />,
    },
    { field: 'startDate', label: t('projects.columns.startDate'), render: p => p.startDate || '—' },
    { field: 'endDate', label: t('projects.columns.endDate'), render: p => p.endDate || '—' },
    lastActivityColumn<any>(t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [statusOptions, templateSteps, t]);

  const exportCsv = () => {
    const header = columns.map(c => c.label);
    const rows = visibleRows.map(p => [
      p.projectNumber, p.description || '', p.companyName || '', p.totalContractAmount || 0,
      getStatusLabel(projectDisplayStatus(p)), p.startDate || '', p.endDate || '',
    ]);
    const csv = [header.join(';'), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';'))].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `projects_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const renderMobileCard = (project: any, { open }: { open: () => void; selected: boolean }) => (
    <div className="card mb-2" onClick={open} style={{ cursor: 'pointer' }}>
      <div className="card-body py-2 px-3">
        <div className="d-flex align-items-center gap-2 mb-1">
          <span className="fw-semibold">#{project.projectNumber}</span>
          <ProjectWorkflowStatusBadge project={project} steps={templateSteps} className="flex-shrink-0" />
        </div>
        <div className="text-truncate mb-1">{project.description}</div>
        <div className="text-muted small">
          <i className="feather-briefcase me-1"></i>
          <ProjectCompanyName companyName={project.companyName} />
        </div>
        <div className="d-flex flex-wrap gap-3 mt-1">
          <span className="text-muted small"><i className="feather-calendar me-1"></i>{project.startDate || '—'} – {project.endDate || '—'}</span>
          <span className="text-muted small"><i className="feather-clock me-1" aria-hidden="true"></i>{t('projects.columns.lastActivityAt')}: {formatLastActivity(project.lastActivityAt, t)}</span>
          {project.totalContractAmount > 0 && (
            <span className="text-muted small"><i className="feather-tag me-1"></i>{formatAmount(project.totalContractAmount)} kr</span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{completed ? t('nav.completedProjects') : t('projects.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: completed ? t('nav.completedProjects') : t('projects.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('projects.description')}</p>
        </div>
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      ) : listedProjects.length === 0 ? (
        <Card>
          {completed ? (
            <div className="text-center py-5 text-muted" data-testid="completed-projects-empty">
              <i className="feather-check-circle fs-1 d-block mb-3" aria-hidden="true"></i>
              <h5 className="fw-bold mb-0">{t('projects.completed.empty')}</h5>
            </div>
          ) : (
            <div className="text-center py-5 px-3" data-testid="person-projects-empty">
              <i className="feather-folder fs-1 text-muted mb-3 d-block" aria-hidden="true"></i>
              <h5 className="fw-bold">{t('personProject.empty.title')}</h5>
              <p className="text-muted mb-1">{t('personProject.empty.text')}</p>
              <p className="text-muted">{t('personProject.empty.invitationHint')}</p>
              <Button variant="light-brand" onClick={() => navigate('/invite')}>
                <i className="feather-send me-2" aria-hidden="true"></i>
                {t('personProject.empty.inviteContractor')}
              </Button>
            </div>
          )}
        </Card>
      ) : (
        <Card>
          <ProjectsListTable
            rows={listedProjects}
            columns={columns}
            rowKey={p => p.id}
            columnOrder={columnOrder}
            onColumnReorder={handleReorder}
            reorderTitle={t('projects.dragToReorder')}
            sort={listSort}
            onSortChange={handleListSort}
            onOpen={openProject}
            actionTitle={t('projects.goToProject', 'Gå till Projekt')}
            emptyText={completed ? t('projects.completed.empty') : t('personProject.empty.title')}
            noResultsText={t('projects.noResults')}
            mobileFilter={{ searchField: 'description', select: completed ? undefined : { field: 'status', allLabel: t('projects.allStatuses'), options: statusOptions }, sort: projectListMobileSort(t) }}
            renderMobileCard={renderMobileCard}
            onVisibleRowsChange={setVisibleRows}
            toolbar={
              <>
                <Button variant="light-brand" onClick={exportCsv}>
                  <i className="feather-download"></i>
                  <span className="d-none d-sm-inline ms-2">{t('projects.export')}</span>
                </Button>
                {completed && (
                  <span className="projects-list-fixed-filter" data-testid="completed-projects-filter">
                    <i className="feather-check-circle" aria-hidden="true"></i>
                    {t('projects.completed.fixedFilter')}
                  </span>
                )}
              </>
            }
          />
        </Card>
      )}
    </DashboardLayout>
  );
}
